import { getCSRFToken } from './csrf';
import { getSessionToken } from '@shopify/app-bridge-utils';
import createApp from '@shopify/app-bridge';

export const RECONNECT_EVENT = 'instaslides:shopify-reconnect';
let reconnectRequest: Promise<string> | undefined;

export class ShopifyReconnectRequired extends Error {
  readonly reauthorizationRequired = true;
  constructor() {
    super('Reconnect Shopify in the notice above, then retry your action.');
  }
}

export function clearReconnectRequest() {
  reconnectRequest = undefined;
}

export function getFetch(url: string, body: any) { return appFetch('GET', url); }
export function postFetch(url: string, body: any) { return appFetch('POST', url, body); }
export function putFetch(url: string, body: any) { return appFetch('PUT', url, body); }
export function deleteFetch(url: string, body: any) { return appFetch('DELETE', url, body); }

async function send(method: string, url: string, body?: any): Promise<Response> {
  const token = await getSessionTokenFromAppBridge();
  return fetch(addSlashToURLWhereNecessary(url), {
    credentials: 'include', method, mode: 'same-origin',
    headers: {
      'Accept': 'application/json', 'Content-Type': 'application/json',
      'X-CSRFToken': getCSRFToken(), 'Authorization': 'Bearer ' + token
    },
    ...(method === 'GET' ? {} : { body: JSON.stringify(body) })
  });
}

export async function prepareShopifyReconnect(): Promise<string> {
  // Share one nonce among concurrent failing requests. Never replay their work.
  if (!reconnectRequest) {
    reconnectRequest = (async () => {
      const response = await send('POST', '/app/api/shopify/reauthorize/', {});
      if (!response.ok) { throw new Error('Could not prepare Shopify authorization.'); }
      const data = await response.json();
      const url = new URL(data.permission_url);
      if (typeof data.shop !== 'string' ||
          !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.myshopify\.com$/.test(data.shop) ||
          url.protocol !== 'https:' || url.hostname !== data.shop || url.port ||
          url.username || url.password || url.pathname !== '/admin/oauth/authorize') {
        throw new Error('Invalid Shopify authorization destination.');
      }
      return url.href;
    })().catch(() => {
      reconnectRequest = undefined;
      throw new Error('Could not prepare Shopify authorization. Please try again.');
    });
  }
  return reconnectRequest;
}

async function appFetch(method: string, url: string, body?: any): Promise<Response> {
  const response = await send(method, url, body);
  if (response.ok) { return response; }
  let data: any = {};
  try { data = await response.clone().json(); } catch (_) { /* No upstream text in errors. */ }
  if (response.status === 401 && data.reauthorization_required === true) {
    let permissionURL: string | undefined;
    try { permissionURL = await prepareShopifyReconnect(); } catch (_) { /* Notice offers retry. */ }
    window.dispatchEvent(new CustomEvent(RECONNECT_EVENT, { detail: { permissionURL } }));
    throw new ShopifyReconnectRequired();
  }
  // Callers must never interpret an HTTP failure as a successful save/delete.
  throw new Error('The request could not be completed. Please try again.');
}

async function getSessionTokenFromAppBridge(): Promise<string> {
  let shopHost = new URLSearchParams(window.location.search).get('host');
  try {
    if (shopHost) { localStorage.setItem('shopHost', shopHost); }
    else { shopHost = localStorage.getItem('shopHost'); }
  } catch (_) {
    if (shopHost) { window.name = shopHost; }
    else { shopHost = window.name; }
  }
  let embedded: boolean;
  try { embedded = window.self !== window.top; } catch (_) { embedded = true; }
  if (!embedded || !shopHost) { throw new Error('Open InstaSlides from Shopify.'); }
  const response = await fetch('/app/api/shopify/non-auth/', {});
  if (!response.ok) { throw new Error('Shopify authorization is unavailable.'); }
  const data = await response.json();
  (window as any).apiKey = data.api_key;
  const app: any = createApp({ host: shopHost, apiKey: data.api_key });
  // Return this request's token, avoiding a mutable global across requests.
  return getSessionToken(app);
}

function addSlashToURLWhereNecessary(url: string) {
  return !url.endsWith('/') && url.indexOf('?') === -1 ? url + '/' : url;
}
